import {getDateTime, moment} from "@/src/modules/core/lib";

import type {
  BriefingBoardCreatePayload,
  BriefingBoardUpdatePayload,
} from "../types";

export type BriefingBoardCreateFormValues = {
  brief: string;
  expires_date?: Date;
  expires_time?: string;
  target_all_properties?: boolean;
  property_ids?: number[];
  target_all_roles?: boolean;
  role_keys?: string[];
  files?: string[];
  /** UI-only — stripped before API */
  properties?: {property_id: number; name: string}[];
  roles?: {key: string; name: string}[];
  /** Legacy field — stripped */
  property_id?: number;
  briefing_board_id?: number;
};

export type BriefingBoardUpdateFormValues = {
  brief: string;
  expires_date?: Date;
  expires_time?: string;
  /** Current server value — used to validate extend-only updates */
  current_expires_at?: string;
  files?: string[];
  briefing_board_id?: number;
};

function serializeExpiresAt(date: Date, time: string): string {
  // getDateTime expects HH:mm and appends :00 for seconds
  const hhmm = time.length >= 5 ? time.slice(0, 5) : time;
  return getDateTime(date, hhmm).utc().format();
}

export function serializeBriefingBoardCreatePayload(
  values: BriefingBoardCreateFormValues,
): BriefingBoardCreatePayload {
  const targetAllProperties = values.target_all_properties ?? false;
  const targetAllRoles = values.target_all_roles ?? false;

  if (!values.expires_date || !values.expires_time) {
    throw new Error("Expiry date and time are required");
  }

  const payload: BriefingBoardCreatePayload = {
    brief: values.brief.trim(),
    expires_at: serializeExpiresAt(values.expires_date, values.expires_time),
    target_all_properties: targetAllProperties,
    target_all_roles: targetAllRoles,
  };

  if (!targetAllProperties && values.property_ids?.length) {
    payload.property_ids = values.property_ids;
  }

  if (!targetAllRoles && values.role_keys?.length) {
    payload.role_keys = values.role_keys;
  }

  if (values.files?.length) {
    payload.files = values.files;
  }

  return payload;
}

export function serializeBriefingBoardUpdatePayload(
  values: BriefingBoardUpdateFormValues,
): BriefingBoardUpdatePayload {
  const payload: BriefingBoardUpdatePayload = {
    brief: values.brief.trim(),
  };

  if (values.expires_date && values.expires_time) {
    payload.expires_at = serializeExpiresAt(values.expires_date, values.expires_time);
  }

  if (values.files?.length) {
    payload.files = values.files;
  }

  return payload;
}
